/**
 * `GET /uploads?ref_num=&source_name=` — what the user actually photographed.
 *
 * Probed live against hegicpp.api (29 rows for ref 23260000881). The response
 * is a dict keyed "0","1",… — not an array — and every row is exactly:
 *
 *   { name, picture, ref_num, source_name }
 *
 * `name` is a CATEGORY here, and that matters: the assessment payload's
 * `name` is a file name, which is why the Images tab shows Hegic's assessment
 * uploads flat. These rows carry real tokens — car_image (22), part_image (2),
 * engine_image (2), chasis_image (2), odo_meter_image (1) — so the tab's
 * existing data-driven grouping starts working with no flag to set.
 *
 * `picture` is /root/media/hdfcergo_pp/<ref>/uploads/<hash>.jpg and the media
 * host serves it verbatim, prefix included, without auth (checked: 200
 * image/jpeg; the same path with /root stripped is a 404). So no re-rooting —
 * that is Bajaj's bucket, not this one.
 *
 * This is also the cheapest image call Hegic has: ~5 KB against a full
 * assessment, which is why the case list uses it for thumbnails.
 */

/** The tokens this endpoint uses, and what to call them. */
export const UPLOAD_TITLES = {
  car_image: "Exterior",
  part_image: "Close-up",
  engine_image: "Engine",
  chasis_image: "Chassis",
  odo_meter_image: "Odometer",
};

const asRows = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object") {
    // The dict is keyed by position; Object.values keeps that order.
    return Object.values(raw.data && typeof raw.data === "object" ? raw.data : raw);
  }
  return [];
};

/**
 * The uploads as the Images tab and the thumbnail want them.
 *
 * Rows with no picture are dropped rather than rendered as a broken image, and
 * the shape matches what toCapturedImages already reads, so nothing downstream
 * has to know which endpoint the images came from.
 */
export const toUploads = (raw) =>
  asRows(raw)
    .filter((row) => row && typeof row === "object")
    .map((row, index) => {
      const picture = typeof row.picture === "string" ? row.picture.trim() : "";
      return {
        id: `upload-${index}`,
        name: typeof row.name === "string" ? row.name : "",
        picture,
        upload_picture: picture,
        refNum: row.ref_num || "",
      };
    })
    .filter((row) => row.picture);

/** The first picture, for a list thumbnail. */
export const firstUploadPicture = (raw) => toUploads(raw)[0]?.picture || null;

export default toUploads;
